import { useCallback, useRef } from 'react';
import { useUndoRedo } from '@/lib/hooks/useUndoRedo';

interface CanvasLike {
  toJSON(): object;
  loadFromJSON(json: object): Promise<void>;
  renderAll(): void;
}

interface UseCanvasHistoryOptions {
  maxHistory?: number;
}

/**
 * Canvas-specific undo/redo built on top of the shared useUndoRedo hook.
 * Snapshots are Fabric.js canvas JSON states.
 */
export function useCanvasHistory({ maxHistory = 50 }: UseCanvasHistoryOptions = {}) {
  const canvasRef = useRef<CanvasLike | null>(null);
  const isRestoringRef = useRef(false);

  const { canUndo, canRedo, undoCount, redoCount, snapshot, undo, redo, clear } =
    useUndoRedo<object>({ maxHistory });

  const setCanvas = useCallback((canvas: CanvasLike | null) => {
    canvasRef.current = canvas;
  }, []);

  /** Capture current canvas state as a history snapshot */
  const saveSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || isRestoringRef.current) return;
    const json = canvas.toJSON();
    snapshot(json);
  }, [snapshot]);

  /** Undo to previous canvas state */
  const undoCanvas = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const prev = undo();
    if (prev) {
      isRestoringRef.current = true;
      try {
        await canvas.loadFromJSON(prev);
        canvas.renderAll();
      } finally {
        isRestoringRef.current = false;
      }
    }
  }, [undo]);

  /** Redo to next canvas state */
  const redoCanvas = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const next = redo();
    if (next) {
      isRestoringRef.current = true;
      try {
        await canvas.loadFromJSON(next);
        canvas.renderAll();
      } finally {
        isRestoringRef.current = false;
      }
    }
  }, [redo]);

  return {
    setCanvas,
    saveSnapshot,
    undoCanvas,
    redoCanvas,
    clearHistory: clear,
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    isRestoring: isRestoringRef,
  };
}
